import { useCallback, useEffect, useState } from 'react';
import { Table, Spin } from 'antd';
import { Button, ModalComponent } from '../../components/shared';
import { translate } from '../../lib/lang.helper';
import { useWidth } from '../../lib/useWidth';
import { selectLang } from '../../store/ui/ui.selector';
import { CaretDownOutlined } from '@ant-design/icons';
import {
  checkRolePermission,
  formatNumber,
} from '../../entities/util-functions';
import { sendSingleRequest } from '../../apis';
import { selectProfile } from '../../store/auth/auth.selector';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showFeedback } from '../../store/ui/ui.actions';
import { TBusinessType } from '../../entities/general.entities';
import { IAssessmentResource } from '../../entities/farms.entities';
import { StageLabelSpan } from '../../components/CommonViews';
import { defaultDateFormat } from '../../lib/common.helpers';

interface ILine {
  id: number;
  crop_type: TBusinessType;
  line_name: string;
  spat_source: string;
  date_seed: number;
  stage: number;
  seed_type: string;
  line_length: number | null;
  basket_count: number | null;
  estimated_amount: number | null;
  drop?: number;
  spacing: string;
  size_min: number;
  size_max: number;
  size_avg: number;
  density: string;
  comment: string;
  last_assessment?: IAssessmentResource;
}

interface IFarm {
  id: string | number;
  owners?: string;
  name: string;
}

interface IDataItem {
  farm: IFarm;
  lines: ILine[];
}

const farmColumns = [
  {
    title: 'Farm',
    dataIndex: ['farm', 'type'],
    key: 'type',
    render: (text: string, record: IDataItem) => (
      <>
        <span>{text}</span>
        {record.farm.owners && (
          <span>{` - Owned by ${record.farm.owners}`}</span>
        )}
      </>
    ),
  },
];

const lineColumns = [
  { title: 'Line', key: 'line_name', render: (x: ILine) => x.line_name },
  {
    title: 'Spat Code',
    key: 'spat_source',
    render: (x: ILine) => x.spat_source,
  },
  {
    title: 'Date Seeded',
    key: 'date_seed',
    render: (x: ILine) => defaultDateFormat(x.date_seed),
  },
  {
    title: 'Stage',
    key: 'stage',
    render: (x: ILine) => <StageLabelSpan stage={x.stage} />,
  },
  { title: 'Type', key: 'seed_type', render: (x: ILine) => x.seed_type },
  {
    title: 'Meters',
    key: 'line_length',
    render: (x: ILine) => x.line_length ?? 'N/A',
  },
  {
    title: 'Drop Depth',
    key: 'drop',
    render: (x: ILine) => x.drop ?? 'N/A',
  },
  {
    title: 'Containers',
    key: 'basket_count',
    render: (x: ILine) => x.basket_count ?? 'N/A',
  },
  { title: 'Spacing', key: 'spacing', render: (x: ILine) => x.spacing },
  {
    title: 'Date Assessed',
    key: 'assessment_date',
    render: (x: ILine) =>
      defaultDateFormat(x.last_assessment?.assessment_date, '-'),
  },
  {
    title: 'Size Min',
    key: 'size_min',
    render: (x: ILine) =>
      x.last_assessment ? (
        <span>
          <div>{x.size_min}</div>
          <div>{' ↓ '}</div>
          <div>{x.last_assessment.shell_size.min}</div>
        </span>
      ) : (
        <span>{x.size_min}</span>
      ),
  },
  {
    title: 'Size Max',
    key: 'size_max',
    render: (x: ILine) =>
      x.last_assessment ? (
        <span>
          <div>{x.size_max}</div>
          <div>{' ↓ '}</div>
          <div>{x.last_assessment.shell_size.max}</div>
        </span>
      ) : (
        <span>{x.size_max}</span>
      ),
  },
  {
    title: 'Size Avg',
    key: 'size_avg',
    render: (x: ILine) =>
      x.last_assessment ? (
        <span>
          <div>{x.size_avg}</div>
          <div>{' ↓ '}</div>
          <div>{x.last_assessment.shell_size.avg}</div>
        </span>
      ) : (
        <span>{x.size_avg}</span>
      ),
  },
  {
    title: `Estimated Harvest`,
    key: 'estimated_amount',
    render: (x: ILine) =>
      x.last_assessment && !!x.last_assessment.estimated_amount ? (
        <span>
          {`${formatNumber(
            x.last_assessment.estimated_amount /
              (x.crop_type === 'MUSSEL'
                ? 1000
                : x.crop_type === 'OYSTER'
                ? 12
                : 1),
          )} ${x.crop_type === 'MUSSEL' ? 't' : 'dz'}`}
        </span>
      ) : !!x.estimated_amount ? (
        <span>
          {`~ ${formatNumber(
            x.estimated_amount /
              (x.crop_type === 'MUSSEL'
                ? 1000
                : x.crop_type === 'OYSTER'
                ? 12
                : 1),
          )} ${x.crop_type === 'MUSSEL' ? 't' : 'dz'}`}
        </span>
      ) : (
        <></>
      ),
  },
  { title: 'Density', key: 'density', render: (x: ILine) => x.density },
];

const FarmSpatsPage = () => {
  const width = useWidth();
  const history = useHistory();
  const profile = useSelector(selectProfile);
  const lang = useSelector(selectLang);
  const dispatch = useDispatch<any>();

  const [data, setData] = useState<IDataItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<(string | number)[]>(
    [],
  );
  const [comment, setComment] = useState<string>();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const response = await sendSingleRequest(
      {},
      'GET',
      'api/farm/growing-spats',
      true,
    );
    if (response.status) {
      setData(response.data);
    } else {
      dispatch(
        showFeedback({
          message: response.data?.message,
          type: 'error',
          isMessage: true,
        }),
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExpand = (expanded: boolean, record: IDataItem) => {
    if (expanded) {
      setExpandedRowKeys([record.farm.id]); // Keep only the current row expanded
    } else {
      setExpandedRowKeys([]); // Collapse all rows
    }
  };
  const tableColumns = [
    ...lineColumns,
    {
      title: 'Comment',
      key: 'comment',
      render: (x: ILine) =>
        !x.comment ? (
          <></>
        ) : (
          <div
            className='btn__modal text-center'
            onKeyDown={() => undefined}
            onClick={() => setComment(x.comment)}
          >
            {translate(lang, 'View')}
          </div>
        ),
    },
  ];

  return (
    <div className='h-calc-80 bg-secondary'>
      <div className='container pos-relative'>
        <div style={{ paddingTop: '40px' }}>
          {checkRolePermission(
            { deniedRoles: ['processing_plant'] },
            profile?.role,
          ) && (
            <div>
              <Button
                size={width > 768 ? 3 : 5}
                type='fill'
                width='small'
                color='blue'
                onClick={() => history.push('/farms')}
              >
                {translate(lang, 'All farms')}
              </Button>
              <Button
                size={width > 768 ? 3 : 5}
                type='fill'
                width='small'
                color='blue'
                className={width > 768 ? 'ml-7' : 'mt-7'}
                onClick={() => history.push('/lines')}
              >
                {translate(lang, 'All Lines')}
              </Button>
              <Button
                size={width > 768 ? 3 : 5}
                type='fill'
                width='small'
                color='blue'
                className={width > 768 ? 'ml-7' : 'mt-7'}
                onClick={() => history.push('/farms/visual-mode')}
              >
                {translate(lang, 'Farms map')}
              </Button>
              {profile?.account_type === 'grower' ? (
                <Button
                  size={width > 768 ? 3 : 5}
                  type='bordered'
                  width='small'
                  color='green'
                  className={width > 768 ? 'ml-7' : 'mt-7'}
                  onClick={() => {}}
                >
                  {translate(lang, 'Import')}
                </Button>
              ) : (
                <Button
                  size={width > 768 ? 3 : 5}
                  type='fill'
                  width='small'
                  color='blue'
                  className={width > 768 ? 'ml-7' : 'mt-7'}
                  onClick={() => history.push('/line-spats')}
                >
                  {translate(lang, 'Spat count')}
                </Button>
              )}
            </div>
          )}
        </div>
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Spin size='large' />
          </div>
        ) : (
          <div style={{ padding: '45px 0' }}>
            <div
              style={{
                textAlign: 'center',
                fontSize: '20px',
                color: '#2F5496',
                marginBottom: '5px',
              }}
            >
              Spat Data By Farms
            </div>
            <Table
              className='table table--isSpats'
              rowKey={record => record.farm.id}
              pagination={false}
              columns={farmColumns}
              dataSource={data}
              expandable={{
                expandedRowRender: record => (
                  <Table
                    className='table table--isLines table--isLinesSpats'
                    rowKey={(x, i) => `${x.id}-${i}`}
                    columns={tableColumns}
                    dataSource={record.lines}
                    pagination={false}
                  />
                ),
                expandIcon: ({ expanded, onExpand, record }) => (
                  <div
                    className='pt-20 pb-20'
                    onClick={event => {
                      event.stopPropagation();
                      onExpand(record, event);
                    }}
                  >
                    <CaretDownOutlined rotate={expanded ? 180 : 0} />
                  </div>
                ),
                expandedRowKeys, // Controlled expanded rows
                onExpand: handleExpand, // Custom expand handler
              }}
            />
          </div>
        )}
      </div>
      <ModalComponent
        visible={comment !== undefined}
        onCancel={() => setComment(undefined)}
        type=''
        title={translate(lang, 'Comment')}
        text={translate(lang, comment ?? '')}
      />
    </div>
  );
};

export default FarmSpatsPage;
